import { Service } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { privatePensionLoadedState } from '../atom';
import { screenNameCustomerApplication } from '../constants';
import { execFilter } from '../external/filters';
import {
  ReturnTypeOf_ClienteListarDadosBancarios,
  ReturnTypeOf_ObterDetalhesFundoPrevidenciaPorCnpjFundo,
  ReturnTypeOf_ObterListaBancosSeguradora,
  ReturnTypeOf_PrevidenciaObterListaParentescoSeguradora,
  ReturnTypeOf_PrevidenciaObterListaSeguradora,
  ReturnTypeOf_PrevidenciaObterParametrosPrevidencia,
  ReturnTypeOf_ProdutosObterProdutosDisponiveis,
} from '../external/types';
import {
  normalizePrivatePension,
  normalizePrivatePensionBankList,
  normalizePrivatePensionClientBankAccountList,
  normalizePrivatePensionDetails,
  normalizePrivatePensionFilters,
  normalizePrivatePensionInsuranceCompanyParameters,
  normalizePrivatePensionInsuranceList,
  normalizePrivatePensionRelatedPartyList,
} from '../normalizer';
import {
  DynamicAssetsQueryParams,
  PrivatePensionEntityNormalized,
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams,
  PrivatePensionClientBankAccountEntityNormalized,
  PrivatePensionInsuranceEntityNormalized,
  PrivatePensionBankEntityNormalized,
  PrivatePensionRelatedPartyEntityNormalized,
  PrivatePensionInsuranceCompanyParametersEntityNormalized,
  PrivatePensionDetailsNormalized,
} from '../types';

export const privatePensionInsuranceCompanyParameters = selectorFamily<
  PrivatePensionInsuranceCompanyParametersEntityNormalized,
  { insuranceCompanyId: number }
>({
  key: 'CustomerApplication/privatePensionInsuranceCompanyParameters',
  get: ({ insuranceCompanyId }) => {
    return async () => {
      const apiData =
        await Service.External.Extranet.Previdencia.V1.previdenciaObterParametrosPrevidencia({
          idSeguradora: insuranceCompanyId,
          screenName: screenNameCustomerApplication,
        });

      const data = normalizePrivatePensionInsuranceCompanyParameters(
        apiData as ReturnTypeOf_PrevidenciaObterParametrosPrevidencia,
        insuranceCompanyId
      );

      return data;
    };
  },
});

export const privatePensionQueryUpdated = selectorFamily<
  { data: PrivatePensionEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'CustomerApplication/privatePensionQueryUpdated',
  get: ({ accountId, filtersToApply, searchTerm, document }) => {
    return async () => {
      const settlementParams: {
        liquidezFinal?: number;
        liquidezInicial?: number;
      } = {
        liquidezFinal: undefined,
        liquidezInicial: undefined,
      };

      if (filtersToApply.privatePensionSettlementPeriodMinimum) {
        settlementParams.liquidezInicial =
          filtersToApply.privatePensionSettlementPeriodMinimum as number;
      }

      if (filtersToApply.privatePensionSettlementPeriodMaximum) {
        settlementParams.liquidezFinal =
          filtersToApply.privatePensionSettlementPeriodMaximum as number;

        if (settlementParams.liquidezFinal === 720) {
          settlementParams.liquidezFinal = 20000;
        }
      }

      const apiData = await Service.External.Extranet.Produtos.V1.produtosObterProdutosDisponiveis({
        filtro: {
          idConta: accountId,
          cpf: document,
          tipoProduto: 6,
          ...settlementParams,
        },
        screenName: screenNameCustomerApplication,
      });

      let data = normalizePrivatePension(apiData as ReturnTypeOf_ProdutosObterProdutosDisponiveis);

      if (searchTerm) {
        filtersToApply.productNameNormalizedSearchTermFilter = searchTerm;
      } else {
        delete filtersToApply.productNameNormalizedSearchTermFilter;
      }

      if (filtersToApply) {
        data = execFilter(data, filtersToApply);
      }

      return { data, meta: blankMetaResponse() };
    };
  },
});

export const privatePensionCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'CustomerApplication/privatePensionCustomFiltersQuery',
  get: () => {
    return async ({ get }) => {
      const { data } = get(privatePensionLoadedState);

      return normalizePrivatePensionFilters(data);
    };
  },
});

export const privatePensionRelatedPartyQuery = selectorFamily<
  { data: PrivatePensionRelatedPartyEntityNormalized[]; meta: MetaResponse },
  { insuranceCompanyId: number }
>({
  key: 'CustomerApplication/privatePensionRelatedPartyQuery',
  get: ({ insuranceCompanyId }) => {
    return async () => {
      const apiData =
        await Service.External.Extranet.Previdencia.V1.previdenciaObterListaParentescoSeguradora({
          solicitacao: {
            idSeguradora: insuranceCompanyId,
          },
          screenName: screenNameCustomerApplication,
        });

      const data = normalizePrivatePensionRelatedPartyList(
        apiData as ReturnTypeOf_PrevidenciaObterListaParentescoSeguradora
      );

      return { data, meta: blankMetaResponse() };
    };
  },
});

export const privatePensionInsuranceQuery = selector<{
  data: PrivatePensionInsuranceEntityNormalized[];
  meta: MetaResponse;
}>({
  key: 'CustomerApplication/privatePensionInsuranceQuery',
  get: async () => {
    const apiData = await Service.External.Extranet.Previdencia.V1.previdenciaObterListaSeguradora({
      screenName: screenNameCustomerApplication,
    });

    const data = normalizePrivatePensionInsuranceList(
      apiData as ReturnTypeOf_PrevidenciaObterListaSeguradora
    );

    return { data, meta: blankMetaResponse() };
  },
});

export const privatePensionBankQuery = selectorFamily<
  {
    data: PrivatePensionBankEntityNormalized[];
    meta: MetaResponse;
  },
  { insuranceCompanyId: number }
>({
  key: 'CustomerApplication/privatePensionBankQuery',
  get: ({ insuranceCompanyId }) => {
    return async () => {
      const apiData =
        await Service.External.Extranet.Previdencia.V1.previdenciaObterListaBancosSeguradora({
          solicitacao: {
            idSeguradora: insuranceCompanyId,
          },
          screenName: screenNameCustomerApplication,
        });

      const data = normalizePrivatePensionBankList(
        apiData as ReturnTypeOf_ObterListaBancosSeguradora
      );

      return { data, meta: blankMetaResponse() };
    };
  },
});

export const privatePensionClientBankAccountsQuery = selectorFamily<
  {
    data: PrivatePensionClientBankAccountEntityNormalized[];
    meta: MetaResponse;
  },
  { accountId: number }
>({
  key: 'CustomerApplication/privatePensionClientBankAccountsQuery',
  get:
    ({ accountId }) =>
    async () => {
      const apiData = await Service.External.Extranet.Cliente.V1.clienteListarDadosBancarios({
        filtro: {
          idContaCorrente: accountId,
        },
        screenName: screenNameCustomerApplication,
      });

      const data = normalizePrivatePensionClientBankAccountList(
        apiData as ReturnTypeOf_ClienteListarDadosBancarios
      );

      return { data, meta: blankMetaResponse() };
    },
});

export const privatePensionDetailsQuery = selectorFamily<
  PrivatePensionDetailsNormalized | null | undefined,
  { cpnj?: string }
>({
  key: 'CustomerApplication/privatePensionDetailsQuery',
  get: ({ cpnj }) => {
    return async () => {
      if (!cpnj) {
        return;
      }

      const apiData =
        await Service.External.Extranet.Previdencia.V1.previdenciaObterDetalhesFundoPrevidenciaPorCnpjFundo(
          {
            pCnpj: cpnj,
            screenName: screenNameCustomerApplication,
          }
        );

      const normalizedData = normalizePrivatePensionDetails(
        apiData as ReturnTypeOf_ObterDetalhesFundoPrevidenciaPorCnpjFundo
      );

      return normalizedData;
    };
  },
});
